/* ==========================================================================
   SCRT — Premium Homepage behaviour
   Vanilla, dependency-free, idempotent (safe if the tag is included twice).
   Everything degrades gracefully: without JS the page is fully readable,
   navigable and shoppable via normal links and form POSTs.
   ========================================================================== */

(function () {
  'use strict';

  if (window.SCRTHome) return;
  window.SCRTHome = { version: '1.0.0' };

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var supportsObserver = 'IntersectionObserver' in window;

  if (!supportsObserver) {
    document.documentElement.classList.add('scrt-no-observer');
  }

  /* --------------------------------------------------------------------
     Utilities
     -------------------------------------------------------------------- */

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    if (!format) return '$' + value;
    return format.replace(/\{\{\s*amount\s*\}\}/, value)
                 .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
                 .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','));
  }

  /* --------------------------------------------------------------------
     Scroll reveal
     -------------------------------------------------------------------- */

  var revealObserver = null;

  function initReveals(scope) {
    var nodes = $$('.scrt-reveal:not(.is-visible), .scrt-band:not(.is-visible), .scrt-layer:not(.is-visible)', scope);
    if (!nodes.length) return;

    if (!supportsObserver || reduceMotion.matches) {
      nodes.forEach(function (n) { n.classList.add('is-visible'); });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }

    nodes.forEach(function (n) { revealObserver.observe(n); });
  }

  /* Cards inside a horizontally scrolling rail sit outside the viewport on the
     x-axis, so an item in the middle of the rail can fail the intersection
     threshold and stay at opacity 0 indefinitely. Revealing a rail reveals every
     card in it at once. */
  var RAIL = '.scrt-uses__rail';

  function reveal(el) {
    el.classList.add('is-visible');
    if (revealObserver) revealObserver.unobserve(el);

    var rail = el.closest ? el.closest(RAIL) : null;
    var group = rail || (el.matches && el.matches(RAIL) ? el : null);
    if (!group) return;

    $$('.scrt-reveal:not(.is-visible)', group).forEach(function (n) {
      n.classList.add('is-visible');
      if (revealObserver) revealObserver.unobserve(n);
    });
  }

  /* --------------------------------------------------------------------
     Announcement bar — dismissible, remembered for the session only
     -------------------------------------------------------------------- */

  /* The overlay header is fixed at the top of the viewport, so it would cover a
     static announcement bar. Publish the bar's real height as a custom property
     and keep it correct through resizes and dismissal. */
  function syncAnnouncementHeight() {
    var bar = $('[data-scrt-announce]') || $('.scrt-announce');
    var h = (bar && !bar.hasAttribute('hidden')) ? Math.round(bar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--scrt-announce-h', h + 'px');
  }

  window.addEventListener('resize', syncAnnouncementHeight);

  function initAnnouncement(scope) {
    syncAnnouncementHeight();
    $$('[data-scrt-announce]', scope).forEach(function (bar) {
      if (bar.dataset.scrtBound) return;
      bar.dataset.scrtBound = '1';

      var key = 'scrt-announce-' + (bar.dataset.scrtAnnounce || 'default');
      var closeBtn = $('[data-scrt-announce-close]', bar);

      try {
        if (sessionStorage.getItem(key) === 'dismissed' && !window.Shopify?.designMode) {
          bar.hidden = true;
          syncAnnouncementHeight();
        }
      } catch (e) { /* storage unavailable — bar simply stays visible */ }

      if (!closeBtn) return;
      closeBtn.addEventListener('click', function () {
        bar.hidden = true;
        syncAnnouncementHeight();   // the overlay header must move up with it
        try { sessionStorage.setItem(key, 'dismissed'); } catch (e) {}
      });
    });
  }

  /* --------------------------------------------------------------------
     Trust marquee — pause control
     Motion that starts on its own and runs past five seconds needs a way to
     stop it. Hover and focus pause it in CSS; this is the explicit control.
     -------------------------------------------------------------------- */

  function initMarquee(scope) {
    $$('[data-scrt-marquee]', scope).forEach(function (bar) {
      if (bar.dataset.scrtBound) return;
      bar.dataset.scrtBound = '1';

      var btn = $('[data-scrt-marquee-pause]', bar);
      if (!btn) return;

      var label = $('[data-scrt-marquee-pause-label]', btn);
      var playText = btn.getAttribute('data-label-play') || 'Resume scrolling credentials';
      var pauseText = label ? label.textContent.trim() : 'Pause scrolling credentials';

      btn.addEventListener('click', function () {
        var paused = bar.classList.toggle('is-paused');
        btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
        if (label) label.textContent = paused ? playText : pauseText;
      });
    });
  }

  /* --------------------------------------------------------------------
     Header — transparent over hero, solid once scrolled
     -------------------------------------------------------------------- */

  function initHeader(scope) {
    $$('[data-scrt-header]', scope).forEach(function (header) {
      if (header.dataset.scrtBound) return;
      header.dataset.scrtBound = '1';

      var threshold = parseInt(header.dataset.scrtStickThreshold || '40', 10);
      var ticking = false;

      function update() {
        header.classList.toggle('is-stuck', window.scrollY > threshold);
        ticking = false;
      }

      function onScroll() {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      }

      update();
      window.addEventListener('scroll', onScroll, { passive: true });

      /* Desktop dropdowns — click to open, Escape and outside-click to close. */
      var toggles = $$('[data-scrt-nav-toggle]', header);

      function closeAll(except) {
        toggles.forEach(function (t) {
          if (t !== except) t.setAttribute('aria-expanded', 'false');
        });
      }

      toggles.forEach(function (toggle) {
        toggle.addEventListener('click', function () {
          var open = toggle.getAttribute('aria-expanded') === 'true';
          closeAll(toggle);
          toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        });

        var item = toggle.closest('.scrt-nav__item');
        if (!item) return;
        item.addEventListener('mouseenter', function () {
          if (window.matchMedia('(hover: hover)').matches) {
            closeAll(toggle);
            toggle.setAttribute('aria-expanded', 'true');
          }
        });
        item.addEventListener('mouseleave', function () {
          if (window.matchMedia('(hover: hover)').matches) {
            toggle.setAttribute('aria-expanded', 'false');
          }
        });
        item.addEventListener('focusout', function (e) {
          if (!item.contains(e.relatedTarget)) toggle.setAttribute('aria-expanded', 'false');
        });
      });

      document.addEventListener('click', function (e) {
        if (!header.contains(e.target)) closeAll(null);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeAll(null);
      });
    });
  }

  /* --------------------------------------------------------------------
     Mobile drawer — focus trapped, Escape closes, scroll locked
     -------------------------------------------------------------------- */

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function initDrawer(scope) {
    $$('[data-scrt-drawer]', scope).forEach(function (drawer) {
      if (drawer.dataset.scrtBound) return;
      drawer.dataset.scrtBound = '1';

      var id = drawer.id;
      var openers = $$('[data-scrt-drawer-open="' + id + '"]');
      var closers = $$('[data-scrt-drawer-close]', drawer);
      var panel = $('.scrt-drawer__panel', drawer);
      var lastFocused = null;

      function open() {
        lastFocused = document.activeElement;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('scrt-no-scroll');
        openers.forEach(function (o) { o.setAttribute('aria-expanded', 'true'); });
        var first = $(FOCUSABLE, panel);
        if (first) first.focus();
      }

      function close() {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('scrt-no-scroll');
        openers.forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
        if (lastFocused && lastFocused.focus) lastFocused.focus();
      }

      openers.forEach(function (o) { o.addEventListener('click', open); });
      closers.forEach(function (c) { c.addEventListener('click', close); });

      drawer.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { close(); return; }
        if (e.key !== 'Tab' || !drawer.classList.contains('is-open')) return;

        var items = $$(FOCUSABLE, panel).filter(function (el) { return el.offsetParent !== null; });
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];

        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });

      drawer.SCRTClose = close;
    });
  }

  /* --------------------------------------------------------------------
     Accordions (drawer submenus + FAQ-style panels)
     -------------------------------------------------------------------- */

  /* --------------------------------------------------------------------
     Parallax
     One rAF-throttled scroll listener for every parallax layer on the page,
     rather than one listener each. Layers are only measured while they are on
     screen, so a section far down the page costs nothing.
     -------------------------------------------------------------------- */

  var parallaxLayers = [];
  var parallaxTicking = false;
  var parallaxBound = false;

  function positionParallax() {
    var vh = window.innerHeight || document.documentElement.clientHeight;

    parallaxLayers.forEach(function (layer) {
      if (!layer.visible) return;

      var section = layer.el.parentElement;
      if (!section) return;

      var rect = section.getBoundingClientRect();

      /* -1 when the section is just below the fold, +1 when it has just left
         the top. Multiply by the depth to get the drift. */
      var progress = (vh - rect.top) / (vh + rect.height) * 2 - 1;
      progress = Math.max(-1, Math.min(1, progress));

      layer.el.style.transform = 'translate3d(0, ' + (progress * layer.depth).toFixed(2) + 'px, 0)';
    });

    parallaxTicking = false;
  }

  function requestParallax() {
    if (parallaxTicking) return;
    parallaxTicking = true;
    requestAnimationFrame(positionParallax);
  }

  function initParallax(scope) {
    if (reduceMotion.matches) return;

    $$('[data-scrt-parallax]', scope).forEach(function (el) {
      if (el.dataset.scrtBound) return;
      el.dataset.scrtBound = '1';

      var depth = parseFloat(el.getAttribute('data-scrt-parallax-depth') || '60');
      if (!depth) return;

      var layer = { el: el, depth: depth, visible: !supportsObserver };
      parallaxLayers.push(layer);

      if (supportsObserver) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            layer.visible = entry.isIntersecting;
            if (entry.isIntersecting) requestParallax();
          });
        }, { rootMargin: '120px 0px' });
        io.observe(el.parentElement || el);
      }
    });

    if (parallaxLayers.length && !parallaxBound) {
      parallaxBound = true;
      window.addEventListener('scroll', requestParallax, { passive: true });
      window.addEventListener('resize', requestParallax);
      requestParallax();
    }
  }

  /* --------------------------------------------------------------------
     Video panel — custom controls
     A browser control bar drops a grey slab across the composition, so the
     video runs without one and this supplies play/pause. Autoplay is muted and
     inline (the only kind mobile browsers permit) and is skipped entirely for
     visitors who ask for reduced motion.
     -------------------------------------------------------------------- */

  function initVideoPanels(scope) {
    $$('[data-scrt-video]', scope).forEach(function (panel) {
      if (panel.dataset.scrtBound) return;
      panel.dataset.scrtBound = '1';

      var video = $('video', panel);
      if (!video) return;

      var btn = $('[data-scrt-video-toggle]', panel);
      var label = $('[data-scrt-video-toggle-label]', panel);
      var pauseText = label ? label.textContent.trim() : 'Pause video';
      var playText = 'Play video';

      function paint() {
        var paused = video.paused;
        panel.classList.toggle('is-paused', paused);
        if (btn) btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
        if (label) label.textContent = paused ? playText : pauseText;
      }

      video.muted = true;                 // required for autoplay to be allowed
      video.setAttribute('playsinline', '');

      var wantsAutoplay = panel.getAttribute('data-autoplay') === 'true';

      if (wantsAutoplay && !reduceMotion.matches) {
        /* Only start once it is actually on screen — autoplaying a video the
           visitor cannot see wastes their bandwidth and battery. */
        if (supportsObserver) {
          var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                var p = video.play();
                if (p && p.catch) p.catch(function () { paint(); });
              } else if (!video.paused) {
                video.pause();
              }
            });
          }, { threshold: 0.25 });
          io.observe(panel);
        } else {
          var pr = video.play();
          if (pr && pr.catch) pr.catch(function () { paint(); });
        }
      }

      if (btn) {
        btn.addEventListener('click', function () {
          if (video.paused) { video.play(); } else { video.pause(); }
        });
      }

      video.addEventListener('play', paint);
      video.addEventListener('pause', paint);
      paint();
    });
  }

  function initAccordions(scope) {
    $$('[data-scrt-accordion]', scope).forEach(function (btn) {
      if (btn.dataset.scrtBound) return;
      btn.dataset.scrtBound = '1';

      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;

      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';

        /* In a single-open group, close whatever else is showing first. */
        var group = btn.closest('[data-scrt-acc-single]');
        if (group && !open) {
          $$('[data-scrt-accordion][aria-expanded="true"]', group).forEach(function (other) {
            if (other === btn) return;
            other.setAttribute('aria-expanded', 'false');
            var otherPanel = document.getElementById(other.getAttribute('aria-controls'));
            if (otherPanel) otherPanel.classList.remove('is-open');
          });
        }

        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.classList.toggle('is-open', !open);
      });
    });
  }

  /* --------------------------------------------------------------------
     Quick add, cart drawer, options modal and predictive search
     all live in scrt-shop.js. They were moved out of this file so there is
     exactly one submit handler per form — two implementations both calling
     preventDefault() meant whichever bound first silently won.
     -------------------------------------------------------------------- */


  /* --------------------------------------------------------------------
     Newsletter — inline confirmation without a page jump
     -------------------------------------------------------------------- */

  function initNewsletter(scope) {
    $$('[data-scrt-newsletter]', scope).forEach(function (form) {
      if (form.dataset.scrtBound) return;
      form.dataset.scrtBound = '1';

      /* Shopify's customer form posts and returns with ?customer_posted=true.
         Scroll the confirmation into view so it is not missed. */
      var success = $('[data-scrt-newsletter-success]', form) || $('[data-scrt-newsletter-success]', form.parentNode);
      if (success && !reduceMotion.matches) {
        success.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  /* --------------------------------------------------------------------
     Boot
     -------------------------------------------------------------------- */

  function initAll(scope) {
    initReveals(scope);
    initAnnouncement(scope);
    initMarquee(scope);
    initHeader(scope);
    initDrawer(scope);
    initAccordions(scope);
    initVideoPanels(scope);
    initParallax(scope);
    initNewsletter(scope);
  }

  window.SCRTHome.init = initAll;
  window.SCRTHome.formatMoney = formatMoney;

  onReady(function () { initAll(document); });

  /* Theme editor: re-bind when a section is re-rendered. */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { initAll(e.target); });
  document.addEventListener('shopify:block:select', function (e) { initAll(e.target); });
})();
